import { useState } from 'react'

/**
 * A draft that CANNOT be edited without invalidating the verdict about it.
 *
 * The rule was already written down — a verdict about a draft the operator has since edited is
 * stale, and a stale PASS is the dangerous kind — but it was enforced by asking every edit site
 * to make a second call, `clearVerdict()`, after its `setSpec`. Two of the five sites in
 * `SchemaBuilder` never made it: the field REMOVE button (which also re-reconciles the key
 * field, so the draft can come out of it naming a different key) and the add-child-field button.
 * Both were written within an hour of the guard itself. The next field control would have missed
 * it too, because nothing about `setSpec` says the second call exists.
 *
 * So the setter is held IN HERE and never returned. An edit site has nothing to call but
 * `updateSpec` or `patch`, and both clear. Forgetting is no longer one of the options.
 *
 * `onEdit` is read from the render that produced the handler, which is the same render that
 * produced the control the operator clicked. It is `clearVerdict`, which only bumps a ref and
 * sets two pieces of state to null, so there is nothing in it a later render could make stale.
 */
export function useGuardedSpec<T>(initial: T, onEdit: () => void) {
  const [spec, setSpec] = useState<T>(initial)
  /** Every edit that needs the previous draft to compute the next one. */
  const updateSpec = (updater: (previous: T) => T) => {
    setSpec(updater)
    onEdit()
  }
  /** The common case: overwrite some fields, keep the rest. */
  const patch = (next: Partial<T>) => updateSpec(previous => ({ ...previous, ...next }))
  return { spec, updateSpec, patch }
}
